// src/pages/StripeSuccessPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { checkStripeAccountStatus } from '../../redux/therapistSlice'

function StripeAccountSuccessPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('checking');
  const [countdown, setCountdown] = useState(3);
  
  const { accountStatus, error } = useSelector((state) => state.stripe);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const result = await dispatch(checkStripeAccountStatus()).unwrap();
        
        // Debug için konsola yazdır
        console.log('Account Status Result:', result);
        
        // Status kontrolü
        if (result.accountReady) {
          setStatus('success');
        } else if (result.status?.detailsSubmitted) {
          setStatus('pending');
        } else {
          setStatus('incomplete');
        }
        
      } catch (error) {
        console.error('Status check error:', error);
        setStatus('error');
      }
    };

    checkStatus();
  }, [dispatch]);

  useEffect(() => {
    if (accountStatus) {
      if (accountStatus.accountReady) {
        setStatus('success');
      } else if (accountStatus.status?.detailsSubmitted) {
        setStatus('pending');
      } else {
        setStatus('incomplete');
      }
    }
  }, [accountStatus]);

  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    }

    if (status === 'success' && countdown === 0) {
      navigate('/TherapistHomePage');
    }
  }, [status, countdown, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        
        {/* Checking State */}
        {status === 'checking' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-6">
              <svg className="animate-spin h-16 w-16 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying Your Account...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your Stripe account setup.
            </p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-6 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Setup Complete!
            </h1>
            <p className="text-gray-600 mb-4">
              Your Stripe account has been successfully configured.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                You can now receive payments from your appointments.
              </p>
            </div>
            <div className="text-sm text-gray-500">
              Redirecting to dashboard in <span className="font-bold text-blue-600">{countdown}</span> seconds...
            </div>
          </div>
        )}

        {/* Pending State */}
        {status === 'pending' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-6 bg-yellow-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Account Under Review
            </h1>
            <p className="text-gray-600 mb-4">
              Your account details have been submitted and are being reviewed.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                This process usually takes 1-2 business days. You'll receive an email once approved.
              </p>
            </div>
            <button
              onClick={() => navigate('/TherapistHomePage')}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}

        {/* Incomplete State */}
        {status === 'incomplete' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-6 bg-orange-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.963-1.333-2.732 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Setup Incomplete
            </h1>
            <p className="text-gray-600 mb-4">
              Your Stripe account setup was not completed.
            </p>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-orange-800">
                Please complete all required information to activate your payment account.
              </p>
            </div>
            <button
              onClick={() => navigate('/TherapistHomePage')}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto w-16 h-16 mb-6 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verification Error
            </h1>
            <p className="text-gray-600 mb-4">
              We couldn't verify your account status.
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            <button
              onClick={() => navigate('/TherapistHomePage')}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

export default StripeAccountSuccessPage;
